import { isEmpty } from 'lodash';

import {
  ChangeEmail,
  ChangePasswordRules,
  ChangeSignUpPassword,
  SetActiveOrganization,
  SetUserData,
  SetOrgEnsuredState,
  ResetStore,
  SetLastOrganizationId,
  SetPlanInfo,
} from '@/constants/mutations';
import { endpoints } from '~/services/amplify-api/endpoints';
import {
  FetchOrganization,
  FetchBilling,
  SendResetPassword,
  FetchUserData,
  SignUp,
  UpdateUserData,
  ChangePassword,
  OrgEnsured,
  ClearStore,
  Logout,
  UpdateBilling,
  UpdateOrganization,
  FetchBillingPortalUrl,
  FetchBillingPlans,
  FetchBillingCheckoutUrl,
  ChangeActiveOrganization,
  FetchTaxIds,
  GetPlansQuota,
} from '@/constants/actions';
import {
  GetOrganizationId,
  GetActiveOrganization,
  GetUserData,
  GetLastOrganizationId,
  GetActiveOrganizationRole,
  GetOrganizations,
  GetOrganizationById,
  HasUnlimitedAccess,
  GetActiveSubscription,
  GetActiveSubscriptionPlan,
  GetIsCrmAllowed,
  GetIsCrmConfigured,
} from '@/constants/getters';
import { ROLE_TYPE } from '@/constants';

const defaultState = () => {
  return {
    signUpPassword: '',
    confirmSignUpPassword: '',
    email: '',
    organizationId: '',
    userData: null,
    url: process.env.BASE_API_URL,
    orgEnsuredState: false,
    lastOrgId: {},
    planInfo: null,
  };
};

export const state = defaultState;

export const mutations = {
  [ResetStore]: (state) => {
    Object.assign(state, {
      ...defaultState(),
      lastOrgId: state.lastOrgId,
    });
  },

  [ChangeSignUpPassword]: (state, data) => {
    state.signUpPassword = data;
  },

  [ChangePasswordRules]: (state, data) => {
    state.passwordRules = data;
  },

  [ChangeEmail]: (state, email) => {
    state.email = email;
  },

  [SetUserData](state, data) {
    state.userData = data;
  },

  [SetActiveOrganization](state, data) {
    state.organizationId = data;
  },

  [SetOrgEnsuredState](state, currentState) {
    state.orgEnsuredState = currentState;
  },

  [SetLastOrganizationId](state, organizationId) {
    state.lastOrgId[state.userData.id] = organizationId;
  },

  [SetPlanInfo](state, quota) {
    state.planInfo = quota || {};
  },
};

/**
 * Important note, we don't use arrow functions here
 * because that would override @var this context and
 * @var this.$axios would result as an undefined variable.
 */
export const actions = {
  [ClearStore]({ commit }) {
    commit(ResetStore);
  },

  /**
   * USER
   */

  async [FetchUserData]({ commit }) {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.user.profile);
      commit(SetUserData, data);

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [UpdateUserData]({ state, commit }, data) {
    try {
      await this.$amplifyApi.patch(endpoints.user.profile, data);

      commit(SetUserData, { ...state.userData, ...data });
    } catch (error) {
      console.error(error);
    }
  },

  async [SignUp]({ commit }, { email, password }) {
    try {
      commit(ChangeEmail, email);

      const { data } = await this.$amplifyApi.post(endpoints.auth.signUp, { email, password });

      state.userData = data;
    } catch (error) {
      console.error(error);
    }
  },

  async [SendResetPassword]({ state }) {
    try {
      const res = await this.$amplifyApi.post(endpoints.auth.resetPassword, { email: state.email });

      return res;
    } catch (error) {
      console.error(error);
    }
  },

  async [ChangePassword](_, data) {
    try {
      await this.$amplifyApi.post(endpoints.user.changePassword, data);
    } catch (error) {
      console.error(error);
    }
  },

  [Logout]({ dispatch }) {
    localStorage.clear();
    dispatch(ClearStore);

    this.$auth.logout();
  },

  /**
   * ORGANIZATION
   */

  async [FetchOrganization]() {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.organization.base);

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [UpdateOrganization]({ commit, state }, newData) {
    try {
      const { data = {} } = await this.$amplifyApi.patch(endpoints.organization.base, newData);
      const index = state.userData?.organizations?.findIndex((organization) => organization.id === data?.id);

      if (index >= 0) {
        const organizations = [...state.userData.organizations];
        organizations[index] = { ...organizations[index], ...data };

        commit(SetUserData, { ...state.userData, organizations });
      }

      return data || {};
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchBilling]() {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.payment.billing);

      return data || {};
    } catch (error) {
      // throws a 404 if no billing information is set;
      if (error.response.status !== 404) {
        console.error(error);
      }
    }
  },

  async [GetPlansQuota]({ commit }) {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.payments.planInfo);
      const activePlan = data.find((plan) => plan.quota.activeUsers);

      commit(SetPlanInfo, activePlan);
    } catch (error) {
      console.error(error);
    }
  },

  async [UpdateBilling](_, billingData) {
    const { taxId, taxKey, ...keys } = billingData;
    try {
      const body = {
        ...keys,
        ...(taxId !== undefined && taxKey !== undefined && { tax: { taxId, taxKey } }),
      };
      const { data } = await this.$amplifyApi.post(endpoints.payment.billing, body);

      return data;
    } catch (error) {
      if (error.response.status === 400) {
        return { error: error.response.data };
      }

      console.error(error);
    }
  },

  async [FetchBillingPortalUrl](_, portalData) {
    try {
      const { data } = await this.$amplifyApi.post(endpoints.payment.portal, portalData);

      return data || {};
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchBillingPlans]() {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.payment.plans);

      return data?.data || [];
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchBillingCheckoutUrl](_, billingData) {
    try {
      const { data } = await this.$amplifyApi.post(endpoints.payment.checkout, billingData);

      return data || {};
    } catch (error) {
      console.error(error);
    }
  },

  async [FetchTaxIds]() {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.payment.taxIds);

      return data?.data || [];
    } catch (error) {
      console.error(error);
    }
  },

  [SetActiveOrganization]({ commit }, organizationId) {
    commit(SetActiveOrganization, organizationId);
  },

  [SetLastOrganizationId]({ commit }, organizationId) {
    commit(SetLastOrganizationId, organizationId);
  },

  [OrgEnsured]({ commit }) {
    commit(SetOrgEnsuredState, true);
  },

  [ChangeActiveOrganization]({ commit }, { organizationId, auth0Id }) {
    commit(SetActiveOrganization, organizationId);
    commit(SetLastOrganizationId, organizationId);

    if (auth0Id) {
      // Setting redirect local storage so the user is redirected to the current page again
      localStorage.setItem('auth.redirect', window.location.pathname);
      this.$auth.loginWith('auth0', { params: { organization: auth0Id } });
    }
  },
};

export const getters = {
  [GetUserData]: (state) => state.userData,

  [GetOrganizationId]: (state) => state.organizationId,

  [GetLastOrganizationId]: (state) => (userId) => state.lastOrgId?.[userId],

  [GetActiveOrganization]: (state) => state.userData?.organizations.find((org) => org.id === state.organizationId),

  [HasUnlimitedAccess]: (state) => !state.planInfo?.config || isEmpty(state.planInfo?.config),

  [GetActiveOrganizationRole]: (state) => (roleName) =>
    state.userData?.organizations
      .find((org) => org.id === state.organizationId)
      ?.roles.find((role) => role.name === roleName),

  [GetOrganizations]: (state) => state.userData?.organizations ?? [],

  [GetOrganizationById]: (state) => (orgId) => state.userData?.organizations.find((org) => org.id === orgId),

  [GetActiveSubscription]: (state) => {
    const organization = state.userData?.organizations?.find((org) => org.id === state.organizationId);
    const planSubscriptions = organization?.subscriptions?.filter((sub) => sub.plans?.some((plan) => plan.type === 'PLAN')) || [];
    const subscription = planSubscriptions.find((sub) => sub.status === 'ACTIVE' || sub.status === 'TRIAL_ACTIVE') || planSubscriptions[0];
    return subscription || {}; // PLAN or EXTENSION;
  },

  [GetActiveSubscriptionPlan]: (_state, { [GetActiveSubscription]: activeSub }) => {
    return activeSub?.plans?.find((plan) => plan.type === 'PLAN') || {};
  },

  [GetIsCrmConfigured]: (_state, { [GetActiveSubscription]: activeSub }) => {
    const extensions = activeSub?.plans?.filter((plan) => plan.type === 'EXTENSION');

    return extensions && extensions.some((extension) => extension.config.crmConnector);
  },

  [GetIsCrmAllowed]: (_state, { [GetIsCrmConfigured]: isCrmConfigured, [GetActiveOrganization]: currentOrg }) => {
    const isAdminOrOwner = currentOrg?.roles.some((role) => [ROLE_TYPE.OWNER, ROLE_TYPE.ADMIN].includes(role.name));

    return isCrmConfigured && isAdminOrOwner;
  },
};
